import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, map, tap } from 'rxjs/operators';

import { TimeService } from '@shared';

import {
    ProblemInstanceService,
    TimeWindowSuggestionService,
    HandleTimeWindowSuggestion,
    TimeWindowSuggestion
} from 'planner-shared';

import { TimeWindowSuggestionView } from '../models/time-window-suggestion-view.model';

@Injectable({
    providedIn: 'root'
})
export class PatientsTimeWindowSuggestionsService {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly timeService = inject(TimeService);
    private readonly timeWindowSuggestionService = inject(TimeWindowSuggestionService);

    private readonly SuggestionLoadLimit = 10;

    public getTimeWindowSuggestions(): Observable<TimeWindowSuggestionView[]> {
        return this.timeWindowSuggestionService.getTimeWindowSuggestions().pipe(
            map(timeWindowSuggestions => {
                const suggestions = timeWindowSuggestions.map(suggestion => this.mapToSuggestionView(suggestion));
                this.timeWindowSuggestionService.setTimeWindowSuggestionsCount(suggestions.length);
                return suggestions;
            })
        );
    }

    public getTimeWindowSuggestionsCount(): Observable<number> {
        return this.timeWindowSuggestionService.getTimeWindowSuggestionsCount();
    }

    public handleSuggestions(ids: string[], accept: boolean = false): Observable<void> {
        const suggestionsToHandle = ids.map(
            id =>
                <HandleTimeWindowSuggestion>{
                    improvementId: id,
                    permanently: true
                }
        );

        return this.timeWindowSuggestionService.getTimeWindowSuggestions().pipe(
            switchMap(suggestions => {
                return this.timeWindowSuggestionService.handleTimeWindowSuggestions(suggestionsToHandle, accept).pipe(
                    switchMap(() => {
                        if (accept) {
                            const handledSuggestions = suggestions.filter(suggestion =>
                                ids.some(id => suggestion.improvementId === id)
                            );
                            return this.updatePatientTasksTimeWindow(handledSuggestions);
                        }
                        return of(null);
                    }),
                    tap(() => {
                        const notHandledSuggestions = suggestions.filter(
                            suggestion => !ids.some(id => suggestion.improvementId === id)
                        );

                        if (!notHandledSuggestions || notHandledSuggestions.length < this.SuggestionLoadLimit) {
                            this.timeWindowSuggestionService.loadTimeWindowSuggestions();
                        } else {
                            suggestions.splice(0, suggestions.length);
                            suggestions.push(...notHandledSuggestions);
                        }
                    })
                );
            })
        );
    }

    private updatePatientTasksTimeWindow(suggestions: TimeWindowSuggestion[]): Observable<void> {
        return this.problemInstanceService.getProblemInstance().pipe(
            tap(result =>
                suggestions.forEach(suggestion => {
                    const task = result.problemInstance.tasks.find(i => i.originalTaskId === suggestion.taskId);
                    if (task) {
                        task.fromTime = this.timeService.convertTime(suggestion.proposedStartTime);
                        task.toTime = this.timeService.convertTime(suggestion.proposedEndTime);
                    }
                })
            ),
            map(() => null)
        );
    }

    private mapToSuggestionView(suggestion: TimeWindowSuggestion): TimeWindowSuggestionView {
        const suggestionView = Object.assign(<TimeWindowSuggestionView>{}, suggestion);
        suggestionView.permanent = true;
        suggestionView.originalStartTime = this.timeService.convertTime(suggestion.originalStartTime, true);
        suggestionView.originalEndTime = this.timeService.convertTime(suggestion.originalEndTime, true);
        suggestionView.proposedStartTime = this.timeService.convertTime(suggestion.proposedStartTime, true);
        suggestionView.proposedEndTime = this.timeService.convertTime(suggestion.proposedEndTime, true);

        return suggestionView;
    }
}
